import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../../config/api';
import { resizeImage, isValidImageUrl, toIsoFromLocal } from './matchManagementUtils';
import LogoPreview from './LogoPreview';

const MatchManagement = () => {
  const [matches, setMatches] = useState([]);
  const [flags, setFlags] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingMatch, setEditingMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [defaultTopics, setDefaultTopics] = useState([]);
  const [apiSettings, setApiSettings] = useState(null);

  const [logoInputMethods, setLogoInputMethods] = useState({
    teamA: 'file',
    teamB: 'file'
  });

  const [logoUrls, setLogoUrls] = useState({
    teamA: '',
    teamB: ''
  });

  const [urlValidation, setUrlValidation] = useState({
    teamA: { isValid: true, message: '' },
    teamB: { isValid: true, message: '' }
  });

  const [urlModal, setUrlModal] = useState({
    show: false,
    team: '',
    url: ''
  });

  const [formData, setFormData] = useState({
    sport: '',
    eventName: '',
    eventType: '',
    teamA: '',
    teamALogo: '',
    teamAFlag: '',
    teamB: '',
    teamBLogo: '',
    teamBFlag: '',
    startTime: '',
    endTime: '',
    description: '',
    streamingLink: '',
    ticketLink: '',
    ticketPrice: '',
    tags: [],
    isPublished: true,
    discussionTopicsMode: 'none',
    discussionTopics: []
  });

  const suggestedSports = [
    'Football', 'Cricket', 'Tennis', 'Basketball', 'Badminton',
    'Volleyball', 'Hockey', 'Rugby', 'Baseball', 'Golf',
    'Boxing', 'Swimming', 'Athletics', 'Cycling', 'Motorsport',
    'Wrestling', 'Archery', 'Bowling', 'Darts', 'Esports', 'Table Tennis'
  ];

  const commonTags = [
    'Friendly', 'League', 'Semi-final', 'Final', 'Quarter-final',
    'Cup', 'Championship', 'Tournament', 'International', 'Domestic',
    'Youth', 'Women', 'Playoff', 'Group Stage', 'Knockout', 'Derby'
  ];

  useEffect(() => {
    fetchMatches();
    fetchFlags();
    fetchDefaultTopics();
    fetchApiSettings();
  }, []);

  const fetchApiSettings = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/match-api-settings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setApiSettings(data);
      }
    } catch (error) {
      console.error('Error fetching API settings:', error);
    }
  };

  useEffect(() => {
    if (showAddForm || urlModal.show) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showAddForm, urlModal.show]);

  const fetchMatches = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/matches`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setMatches(data);
      }
    } catch (error) {
      console.error('Error fetching matches:', error);
    }
    setLoading(false);
  };

  const fetchFlags = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/flags`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setFlags(data);
      }
    } catch (error) {
      console.error('Error fetching flags:', error);
    }
  };

  const fetchDefaultTopics = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/default-discussion-topics`);
      if (response.ok) {
        const data = await response.json();
        setDefaultTopics(data);
      }
    } catch (error) {
      console.error('Error fetching default topics:', error);
      setDefaultTopics([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const token = localStorage.getItem('adminToken');
      const url = editingMatch 
        ? `${API_BASE_URL}/api/admin/matches/${editingMatch._id}`
        : `${API_BASE_URL}/api/admin/matches`;
      const method = editingMatch ? 'PUT' : 'POST';

      let finalDiscussionTopics = [];
      if (formData.discussionTopicsMode === 'default') {
        finalDiscussionTopics = defaultTopics;
      } else if (formData.discussionTopicsMode === 'custom') {
        finalDiscussionTopics = formData.discussionTopics.filter(topic => topic.trim() !== '');
      }

      const tzOffsetMinutes = typeof formData.startTime === 'string' && formData.startTime
        ? -new Date(formData.startTime).getTimezoneOffset()
        : -new Date().getTimezoneOffset();

      const submitData = {
        ...formData,
        teamAFlag: formData.teamAFlag || null,
        teamBFlag: formData.teamBFlag || null,
        discussionTopics: finalDiscussionTopics,
        startTime: toIsoFromLocal(formData.startTime),
        endTime: toIsoFromLocal(formData.endTime),
        tzOffsetMinutes,
        discussionTopicsMode: undefined,
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(submitData),
      });

      if (response.ok) {
        fetchMatches();
        resetForm();
        setMessage(`Match ${editingMatch ? 'updated' : 'created'} successfully!`);
        setTimeout(() => setMessage(''), 3000);
      } else {
        const errorData = await response.json();
        setMessage(errorData.message || 'Error saving match');
      }
    } catch (error) {
      console.error('Error saving match:', error);
      setMessage('Network error. Please try again.');
    }

    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this match?')) return;

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/matches/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        fetchMatches();
        setMessage('Match deleted successfully!');
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error deleting match:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      sport: '',
      eventName: '',
      eventType: '',
      teamA: '',
      teamB: '',
      teamALogo: '',
      teamBLogo: '',
      teamAFlag: '',
      teamBFlag: '',
      startTime: '',
      endTime: '',
      description: '',
      streamingLink: '',
      ticketLink: '',
      ticketPrice: '',
      tags: [],
      isPublished: true,
      discussionTopicsMode: 'none',
      discussionTopics: []
    });

    setLogoInputMethods({ teamA: 'file', teamB: 'file' });
    setLogoUrls({ teamA: '', teamB: '' });
    setUrlValidation({
      teamA: { isValid: true, message: '' },
      teamB: { isValid: true, message: '' }
    });
    setUrlModal({ show: false, team: '', url: '' });
    setShowAddForm(false);
    setEditingMatch(null);
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const handleEdit = (match) => {
    let mode = 'none';
    if (match.discussionTopics && match.discussionTopics.length > 0) {
      const matchesDefault = defaultTopics.length > 0 &&
        match.discussionTopics.length === defaultTopics.length &&
        match.discussionTopics.every((topic, index) => topic === defaultTopics[index]);
      mode = matchesDefault ? 'default' : 'custom';
    }

    const teamAIsUrl = match.teamALogo && (match.teamALogo.startsWith('http') || match.teamALogo.startsWith('https'));
    const teamBIsUrl = match.teamBLogo && (match.teamBLogo.startsWith('http') || match.teamBLogo.startsWith('https'));

    setLogoInputMethods({
      teamA: teamAIsUrl ? 'url' : 'file',
      teamB: teamBIsUrl ? 'url' : 'file'
    });

    setLogoUrls({
      teamA: teamAIsUrl ? match.teamALogo : '',
      teamB: teamBIsUrl ? match.teamBLogo : ''
    });

    setUrlValidation({
      teamA: { isValid: true, message: '' },
      teamB: { isValid: true, message: '' }
    });

    setFormData({
      ...match,
      eventName: match.eventName || match.league || '',
      eventType: match.eventType || match.venue || '',
      teamAFlag: match.teamAFlag?._id || '',
      teamBFlag: match.teamBFlag?._id || '',
      startTime: formatDateForInput(match.startTime),
      endTime: formatDateForInput(match.endTime),
      discussionTopicsMode: mode,
      discussionTopics: mode === 'custom' ? match.discussionTopics : []
    });

    setEditingMatch(match);
    setShowAddForm(true);
  };

  const handleTagToggle = (tag) => {
    const tags = formData.tags.includes(tag)
      ? formData.tags.filter(t => t !== tag)
      : [...formData.tags, tag];
    setFormData({ ...formData, tags });
  };

  const getStatusBadge = (status) => {
    const badges = {
      'scheduled': 'bg-yellow-100 text-yellow-800',
      'live': 'bg-red-100 text-red-800',
      'ended': 'bg-gray-100 text-gray-800'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  const handleImgUrlClick = (team) => {
    setLogoInputMethods(prev => ({
      ...prev,
      [team]: 'url'
    }));
    setUrlModal({
      show: true,
      team: team,
      url: logoUrls[team] || ''
    });
  };

  const handleUrlModalSubmit = () => {
    const { team, url } = urlModal;
    handleLogoUrlChange(team, url);
    setUrlModal({ show: false, team: '', url: '' });
  };

  const handleUrlModalCancel = () => {
    setUrlModal({ show: false, team: '', url: '' });
  };

  const handleLogoInputMethodChange = (team, method) => {
    setLogoInputMethods(prev => ({
      ...prev,
      [team]: method
    }));

    setUrlValidation(prev => ({
      ...prev,
      [team]: { isValid: true, message: '' }
    }));

    if (method === 'url') {
      setFormData(prev => ({
        ...prev,
        [`${team}Logo`]: ''
      }));
    } else {
      setLogoUrls(prev => ({
        ...prev,
        [team]: ''
      }));
      setFormData(prev => ({
        ...prev,
        [`${team}Logo`]: ''
      }));
    }
  };

  const handleLogoUrlChange = (team, url) => {
    setLogoUrls(prev => ({
      ...prev,
      [team]: url
    }));

    if (!url.trim()) {
      setFormData(prev => ({
        ...prev,
        [`${team}Logo`]: ''
      }));
      setUrlValidation(prev => ({
        ...prev,
        [team]: { isValid: true, message: '' }
      }));
    } else {
      const isValid = isValidImageUrl(url);
      if (isValid) {
        setFormData(prev => ({
          ...prev,
          [`${team}Logo`]: url
        }));
        setUrlValidation(prev => ({
          ...prev,
          [team]: { isValid: true, message: 'Valid image URL' }
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [`${team}Logo`]: ''
        }));
        setUrlValidation(prev => ({
          ...prev,
          [team]: { isValid: false, message: 'Please enter a valid image URL' }
        }));
      }
    }
  };

  const handleImageUpload = async (e, team) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setMessage('Please select a valid image file');
      return;
    }

    setUploading(true);
    setMessage('Processing image...');

    try {
      const resizedImage = await resizeImage(file, 50);
      setFormData(prev => ({
        ...prev,
        [`${team}Logo`]: resizedImage
      }));
      setMessage('Image uploaded successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error processing image:', error);
      setMessage('Error processing image. Please try again.');
    }

    setUploading(false);
  };

  const handleTeamNameChange = (team, value) => {
    setFormData(prev => ({ ...prev, [`team${team}`]: value }));

    const matchingFlag = flags.find(flag => 
      flag.name.toLowerCase() === value.toLowerCase()
    );

    if (matchingFlag) {
      setFormData(prev => ({
        ...prev,
        [`team${team}`]: value,
        [`team${team}Flag`]: matchingFlag._id
      }));
    }
  };

  const handleDiscussionModeChange = (mode) => {
    setFormData(prev => ({
      ...prev,
      discussionTopicsMode: mode,
      discussionTopics: mode === 'custom' ? [''] : []
    }));
  };

  const handleTopicChange = (index, value) => {
    const newTopics = [...formData.discussionTopics];
    newTopics[index] = value;
    setFormData({ ...formData, discussionTopics: newTopics });
  };

  const addTopic = () => {
    setFormData({
      ...formData,
      discussionTopics: [...formData.discussionTopics, '']
    });
  };

  const removeTopic = (index) => {
    const newTopics = formData.discussionTopics.filter((_, i) => i !== index);
    setFormData({ ...formData, discussionTopics: newTopics });
  };

  const clearAllTopics = () => {
    setFormData(prev => ({
      ...prev,
      discussionTopics: ['']
    }));
  };

  // LogoPreview extracted to LogoPreview.js

  return (
    <div className="p-3 sm:p-4 lg:p-6 min-h-screen bg-gray-50">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Match Management</h1>
        <button
          onClick={() => setShowAddForm(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors w-full sm:w-auto text-center"
        >
          Add New Match
        </button>
      </div>

      {/* API Status Banner */}
      {apiSettings && (
        <div className={`mb-4 p-3 rounded-lg text-sm flex items-center justify-between ${
          apiSettings.sofaScoreEnabled 
            ? 'bg-purple-100 border border-purple-300' 
            : 'bg-gray-100 border border-gray-300'
        }`}>
          <div className="flex items-center space-x-3">
            <span className="text-lg">🌐</span>
            <div>
              <span className={`font-medium ${apiSettings.sofaScoreEnabled ? 'text-purple-800' : 'text-gray-700'}`}>
                SofaScore API: {apiSettings.sofaScoreEnabled ? 'Enabled' : 'Disabled'}
              </span>
              {apiSettings.sofaScoreEnabled && (
                <span className="text-purple-600 text-xs ml-2">
                  ({apiSettings.sofaScoreSports?.join(', ')})
                </span>
              )}
            </div>
          </div>
          <a 
            href="#" 
            onClick={(e) => {
              e.preventDefault();
              // Navigate to match-api-settings tab
              const event = new CustomEvent('changeAdminTab', { detail: 'match-api-settings' });
              window.dispatchEvent(event);
            }}
            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            Configure →
          </a>
        </div>
      )}

      {/* Message Alert */}
      {message && (
        <div className={`mb-4 p-3 sm:p-4 rounded-lg text-sm sm:text-base ${
          message.includes('Error') || message.includes('error') 
            ? 'bg-red-100 text-red-700' 
            : 'bg-green-100 text-green-700'
        }`}>
          {message}
        </div>
      )}

      {/* URL Input Modal */}
      {urlModal.show && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              handleUrlModalCancel();
            }
          }}
        >
          <div 
            className="bg-white rounded-lg w-full max-w-md shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-semibold mb-4">
                Enter Image URL for {urlModal.team === 'teamA' ? 'Team A' : 'Team B'}
              </h3>
              <input
                type="url"
                value={urlModal.url}
                onChange={(e) => setUrlModal({ ...urlModal, url: e.target.value })}
                placeholder="https://example.com/image.jpg"
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                autoFocus
              />
              <div className="flex flex-col sm:flex-row justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={handleUrlModalCancel}
                  className="w-full sm:w-auto px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleUrlModalSubmit}
                  className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Apply URL
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Form Modal - REORGANIZED FIELDS */}
      {showAddForm && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[9998] overflow-y-auto"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              resetForm();
            }
          }}
        >
          <div className="min-h-screen px-2 py-4 sm:px-4 sm:py-6 flex items-start justify-center">
            <div 
              className="bg-white rounded-lg w-full max-w-4xl shadow-2xl my-4"
              onClick={(e) => e.stopPropagation()}
            >
              <form onSubmit={handleSubmit} className="p-4 sm:p-6">
                {/* Form Header */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6 pb-4 border-b">
                  <h2 className="text-xl sm:text-2xl font-bold">
                    {editingMatch ? 'Edit Match' : 'Add New Match'}
                  </h2>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="self-end sm:self-auto text-gray-500 hover:text-gray-700 p-2 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <span className="text-2xl">✕</span>
                  </button>
                </div>

                {/* SECTION 1: Basic Match Information */}
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b">Match Information</h3>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                    {/* 1. Sport */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        1. Sport *
                      </label>
                      <input
                        list="sports"
                        type="text"
                        required
                        value={formData.sport}
                        onChange={(e) => setFormData({...formData, sport: e.target.value})}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                        placeholder="Enter or select sport"
                      />
                      <datalist id="sports">
                        {suggestedSports.map(sport => (
                          <option key={sport} value={sport} />
                        ))}
                      </datalist>
                    </div>

                    {/* 2. Event Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        2. Event Name
                      </label>
                      <input
                        type="text"
                        value={formData.eventName}
                        onChange={(e) => setFormData({...formData, eventName: e.target.value})}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                        placeholder="e.g., Premier League, World Cup"
                      />
                    </div>

                    {/* 3. Event Type */}
                    <div className="lg:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        3. Event Type/Venue
                      </label>
                      <input
                        type="text"
                        value={formData.eventType}
                        onChange={(e) => setFormData({...formData, eventType: e.target.value})}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                        placeholder="e.g., Stadium Name, Arena, Online"
                      />
                    </div>
                  </div>
                </div>

                {/* SECTION 2: Team A Details */}
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b">Team A Details</h3>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                    {/* 4. Team A Name */}
                    <div className="lg:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        4. Team A Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.teamA}
                        onChange={(e) => handleTeamNameChange('A', e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                        placeholder="Enter Team A name"
                      />
                    </div>

                    {/* 5. Team A Logo */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        5. Team A Logo
                      </label>
                      <div className="flex flex-wrap gap-2 mb-3">
                        <button
                          type="button"
                          onClick={() => handleLogoInputMethodChange('teamA', 'file')}
                          className={`px-3 py-2 rounded text-xs sm:text-sm flex-1 sm:flex-none transition-colors ${
                            logoInputMethods.teamA === 'file' 
                              ? 'bg-blue-600 text-white' 
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          Upload File
                        </button>
                        <button
                          type="button"
                          onClick={() => handleImgUrlClick('teamA')}
                          className={`px-3 py-2 rounded text-xs sm:text-sm flex-1 sm:flex-none transition-colors ${
                            logoInputMethods.teamA === 'url' 
                              ? 'bg-blue-600 text-white' 
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          IMG URL
                        </button>
                      </div>

                      {logoInputMethods.teamA === 'file' && (
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageUpload(e, 'teamA')}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        />
                      )}

                      {formData.teamALogo && (
                        <div className="mt-3 flex justify-center sm:justify-start">
                          <LogoPreview logoData={formData.teamALogo} alt="Team A Logo" team="teamA" />
                        </div>
                      )}
                    </div>

                    {/* 6. Team A Flag */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        6. Team A Flag
                      </label>
                      <select
                        value={formData.teamAFlag}
                        onChange={(e) => setFormData({...formData, teamAFlag: e.target.value})}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                      >
                        <option value="">Select flag (optional)</option>
                        {flags.map(flag => (
                          <option key={flag._id} value={flag._id}>{flag.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* SECTION 3: Team B Details */}
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b">Team B Details</h3>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                    {/* 7. Team B Name */}
                    <div className="lg:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        7. Team B Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.teamB}
                        onChange={(e) => handleTeamNameChange('B', e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                        placeholder="Enter Team B name"
                      />
                    </div>

                    {/* 8. Team B Logo */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        8. Team B Logo
                      </label>
                      <div className="flex flex-wrap gap-2 mb-3">
                        <button
                          type="button"
                          onClick={() => handleLogoInputMethodChange('teamB', 'file')}
                          className={`px-3 py-2 rounded text-xs sm:text-sm flex-1 sm:flex-none transition-colors ${
                            logoInputMethods.teamB === 'file' 
                              ? 'bg-blue-600 text-white' 
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          Upload File
                        </button>
                        <button
                          type="button"
                          onClick={() => handleImgUrlClick('teamB')}
                          className={`px-3 py-2 rounded text-xs sm:text-sm flex-1 sm:flex-none transition-colors ${
                            logoInputMethods.teamB === 'url' 
                              ? 'bg-blue-600 text-white' 
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          IMG URL
                        </button>
                      </div>

                      {logoInputMethods.teamB === 'file' && (
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageUpload(e, 'teamB')}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        />
                      )}

                      {formData.teamBLogo && (
                        <div className="mt-3 flex justify-center sm:justify-start">
                          <LogoPreview logoData={formData.teamBLogo} alt="Team B Logo" team="teamB" />
                        </div>
                      )}
                    </div>

                    {/* 9. Team B Flag */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        9. Team B Flag
                      </label>
                      <select
                        value={formData.teamBFlag}
                        onChange={(e) => setFormData({...formData, teamBFlag: e.target.value})}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                      >
                        <option value="">Select flag (optional)</option>
                        {flags.map(flag => (
                          <option key={flag._id} value={flag._id}>{flag.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* SECTION 4: Schedule & Timing */}
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b">Schedule & Timing</h3>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                    {/* 10. Start Time */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        10. Start Time *
                      </label>
                      <input
                        type="datetime-local"
                        required
                        value={formData.startTime}
                        onChange={(e) => setFormData({...formData, startTime: e.target.value})}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                      />
                    </div>

                    {/* 11. End Time */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        11. End Time
                      </label>
                      <input
                        type="datetime-local"
                        value={formData.endTime}
                        onChange={(e) => setFormData({...formData, endTime: e.target.value})}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                      />
                    </div>
                  </div>
                </div>

                {/* SECTION 5: Additional Details */}
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b">Additional Details</h3>
                  <div className="space-y-4">
                    {/* 12. Description */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        12. Description
                      </label>
                      <textarea
                        rows={3}
                        value={formData.description}
                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                        placeholder="Match description or additional information"
                      />
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                      {/* 13. Streaming Link */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          13. Streaming Link
                        </label>
                        <input
                          type="url"
                          value={formData.streamingLink}
                          onChange={(e) => setFormData({...formData, streamingLink: e.target.value})}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                          placeholder="https://example.com/stream"
                        />
                      </div>

                      {/* 14. Ticket Link */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          14. Ticket Link
                        </label>
                        <input
                          type="url"
                          value={formData.ticketLink}
                          onChange={(e) => setFormData({...formData, ticketLink: e.target.value})}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                          placeholder="https://example.com/tickets"
                        />
                      </div>

                      {/* 15. Ticket Price */}
                      <div className="lg:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          15. Ticket Price
                        </label>
                        <input
                          type="text"
                          value={formData.ticketPrice}
                          onChange={(e) => setFormData({...formData, ticketPrice: e.target.value})}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                          placeholder="e.g., $25-100, Free, Contact for pricing"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* SECTION 6: Tags */}
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b">Tags & Categories</h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      16. Tags (Select all that apply)
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {commonTags.map(tag => (
                        <button
                          key={tag}
                          type="button"
                          onClick={() => handleTagToggle(tag)}
                          className={`px-3 py-2 rounded-full text-xs sm:text-sm transition-colors ${
                            formData.tags.includes(tag)
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          {tag}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* SECTION 7: Discussion Topics */}
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b">Discussion Topics</h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      17. Discussion Topics Configuration
                    </label>
                    <div className="space-y-3 mb-4">
                      <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="radio"
                          name="discussionMode"
                          value="none"
                          checked={formData.discussionTopicsMode === 'none'}
                          onChange={(e) => handleDiscussionModeChange(e.target.value)}
                          className="mt-1"
                        />
                        <div>
                          <span className="text-sm sm:text-base font-medium">No discussion topics</span>
                          <p className="text-xs text-gray-500 mt-1">Users won't have predefined topics to discuss</p>
                        </div>
                      </label>
                      <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="radio"
                          name="discussionMode"
                          value="default"
                          checked={formData.discussionTopicsMode === 'default'}
                          onChange={(e) => handleDiscussionModeChange(e.target.value)}
                          className="mt-1"
                        />
                        <div>
                          <span className="text-sm sm:text-base font-medium">Use default topics ({defaultTopics.length} topics)</span>
                          <p className="text-xs text-gray-500 mt-1">Apply system-wide default discussion topics</p>
                        </div>
                      </label>
                      <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="radio"
                          name="discussionMode"
                          value="custom"
                          checked={formData.discussionTopicsMode === 'custom'}
                          onChange={(e) => handleDiscussionModeChange(e.target.value)}
                          className="mt-1"
                        />
                        <div>
                          <span className="text-sm sm:text-base font-medium">Custom topics</span>
                          <p className="text-xs text-gray-500 mt-1">Create match-specific discussion topics</p>
                        </div>
                      </label>
                    </div>

                    {formData.discussionTopicsMode === 'custom' && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                        <div className="space-y-3">
                          {formData.discussionTopics.map((topic, index) => (
                            <div key={index} className="flex flex-col sm:flex-row gap-2">
                              <input
                                type="text"
                                value={topic}
                                onChange={(e) => handleTopicChange(index, e.target.value)}
                                placeholder={`Discussion topic ${index + 1}`}
                                className="flex-1 p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                              />
                              <button
                                type="button"
                                onClick={() => removeTopic(index)}
                                className="px-4 py-2 text-red-600 hover:bg-red-50 rounded text-sm sm:text-base transition-colors"
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2 mt-4">
                          <button
                            type="button"
                            onClick={addTopic}
                            className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm sm:text-base transition-colors"
                          >
                            + Add Topic
                          </button>
                          {formData.discussionTopics.length > 1 && (
                            <button
                              type="button"
                              onClick={clearAllTopics}
                              className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 text-sm sm:text-base transition-colors"
                            >
                              Clear All
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {formData.discussionTopicsMode === 'default' && defaultTopics.length > 0 && (
                      <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                        <p className="text-sm font-medium text-blue-900 mb-2">Default topics that will be used:</p>
                        <ul className="list-disc list-inside space-y-1 text-sm text-blue-800">
                          {defaultTopics.slice(0, 5).map((topic, index) => (
                            <li key={index}>{topic}</li>
                          ))}
                          {defaultTopics.length > 5 && (
                            <li className="text-blue-600">... and {defaultTopics.length - 5} more topics</li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>

                {/* SECTION 8: Publishing */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b">Publishing Options</h3>
                  <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.isPublished}
                      onChange={(e) => setFormData({...formData, isPublished: e.target.checked})}
                      className="mt-1"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-700">18. Publish immediately</span>
                      <p className="text-xs text-gray-500 mt-1">Make this match visible to users right away</p>
                    </div>
                  </label>
                </div>

                {/* Form Actions */}
                <div className="flex flex-col sm:flex-row justify-end gap-3 pt-6 border-t">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="w-full sm:w-auto px-6 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading || uploading}
                    className="w-full sm:w-auto px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                  >
                    {loading ? 'Saving...' : (editingMatch ? 'Update Match' : 'Create Match')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Matches Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-base sm:text-lg font-semibold">
            Manage Sports Matches
          </h2>
        </div>

        {/* Mobile Card View */}
        <div className="block lg:hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="flex justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            </div>
          ) : matches.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No matches found. Create your first match!
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {matches.map(match => (
                <div key={match._id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2 flex-1">
                      {match.teamAFlag?.image && (
                        <img
                          src={match.teamAFlag.image}
                          alt={`${match.teamA} flag`}
                          className="w-5 h-3 object-cover rounded"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      )}
                      {match.teamALogo && (
                        <img
                          src={match.teamALogo}
                          alt={`${match.teamA} logo`}
                          className="w-6 h-6 object-cover rounded"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      )}
                      <span className="font-medium text-sm">{match.teamA}</span>
                    </div>
                    <span className="text-gray-500 text-sm mx-2">vs</span>
                    <div className="flex items-center gap-2 flex-1 justify-end">
                      <span className="font-medium text-sm">{match.teamB}</span>
                      {match.teamBLogo && (
                        <img
                          src={match.teamBLogo}
                          alt={`${match.teamB} logo`}
                          className="w-6 h-6 object-cover rounded"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      )}
                      {match.teamBFlag?.image && (
                        <img
                          src={match.teamBFlag.image}
                          alt={`${match.teamB} flag`}
                          className="w-5 h-3 object-cover rounded"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      )}
                    </div>
                  </div>

                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex justify-between">
                      <span>Sport:</span>
                      <span className="font-medium">{match.sport}</span>
                    </div>
                    {(match.eventName || match.league) && (
                      <div className="flex justify-between">
                        <span>Event:</span>
                        <span className="font-medium">{match.eventName || match.league}</span>
                      </div>
                    )}
                    {(match.eventType || match.venue) && (
                      <div className="flex justify-between">
                        <span>Type:</span>
                        <span className="font-medium">{match.eventType || match.venue}</span>
                      </div>
                    )}
                    {match.startTime && (
                      <div className="flex justify-between">
                        <span>Date:</span>
                        <span className="font-medium">
                          {new Date(match.startTime).toLocaleDateString()} {new Date(match.startTime).toLocaleTimeString()}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span>Discussion:</span>
                      <span className={match.discussionTopics && match.discussionTopics.length > 0 ? 'text-green-600' : 'text-gray-500'}>
                        {match.discussionTopics && match.discussionTopics.length > 0 
                          ? `${match.discussionTopics.length} topics` 
                          : 'No topics'}
                      </span>
                    </div>
                  </div>

                  <div className="flex justify-between items-center mt-3 pt-3 border-t">
                    <div className="flex flex-wrap gap-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(match.status)}`}>
                        {match.status || 'scheduled'}
                      </span>
                      {match.isPublished && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Published
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(match)}
                        className="text-blue-600 hover:text-blue-800 text-sm px-3 py-1 hover:bg-blue-50 rounded transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(match._id)}
                        className="text-red-600 hover:text-red-800 text-sm px-3 py-1 hover:bg-red-50 rounded transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Desktop Table View */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Match
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sport/Event/Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Schedule
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Discussion
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  </td>
                </tr>
              ) : matches.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                    No matches found. Create your first match!
                  </td>
                </tr>
              ) : (
                matches.map(match => (
                  <tr key={match._id} className="hover:bg-gray-50">
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {match.teamAFlag?.image && (
                            <img
                              src={match.teamAFlag.image}
                              alt={`${match.teamA} flag`}
                              className="w-6 h-4 object-cover rounded"
                              onError={(e) => e.target.style.display = 'none'}
                            />
                          )}
                          {match.teamALogo && (
                            <img
                              src={match.teamALogo}
                              alt={`${match.teamA} logo`}
                              className="w-8 h-8 object-cover rounded"
                              onError={(e) => e.target.style.display = 'none'}
                            />
                          )}
                          <span className="font-medium">{match.teamA}</span>
                        </div>
                        <span className="text-gray-500 mx-2">vs</span>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{match.teamB}</span>
                          {match.teamBLogo && (
                            <img
                              src={match.teamBLogo}
                              alt={`${match.teamB} logo`}
                              className="w-8 h-8 object-cover rounded"
                              onError={(e) => e.target.style.display = 'none'}
                            />
                          )}
                          {match.teamBFlag?.image && (
                            <img
                              src={match.teamBFlag.image}
                              alt={`${match.teamB} flag`}
                              className="w-6 h-4 object-cover rounded"
                              onError={(e) => e.target.style.display = 'none'}
                            />
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="font-medium">{match.sport}</div>
                      {(match.eventName || match.league) && (
                        <div className="text-sm text-gray-500">
                          {match.eventName || match.league}
                        </div>
                      )}
                      {(match.eventType || match.venue) && (
                        <div className="text-sm text-gray-500">
                          {match.eventType || match.venue}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      {match.startTime && (
                        <>
                          <div className="text-sm">
                            {new Date(match.startTime).toLocaleDateString()}
                          </div>
                          <div className="text-sm text-gray-500">
                            {new Date(match.startTime).toLocaleTimeString()}
                          </div>
                        </>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      {match.discussionTopics && match.discussionTopics.length > 0 ? (
                        <span className="text-sm text-green-600 font-medium">
                          {match.discussionTopics.length} topics
                        </span>
                      ) : (
                        <span className="text-sm text-gray-500">
                          No topics
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex flex-wrap gap-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(match.status)}`}>
                          {match.status || 'scheduled'}
                        </span>
                        {match.isPublished && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Published
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex gap-3">
                        <button
                          onClick={() => handleEdit(match)}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(match._id)}
                          className="text-red-600 hover:text-red-800 text-sm font-medium"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MatchManagement;
